import React, { useEffect, useMemo, useRef, useState } from 'react'
import Button from '../../../../../ui/Button/Button'
import CatalogTaxonomyNodePicker from '../../../productSchema/CatalogTaxonomyNodePicker'
import { buildCatalogTaxonomyNodePathLabel, buildCatalogTaxonomyNodePathSegments } from '../../../productSchema/formatProductTaxonomySummary'
import { splitMainAndExtraAssignments } from '../../../productSchema/productTaxonomyUx'
import {
  applyPrimaryBulkCategorization,
  countProductsWithMain,
} from './applyBulkProductTaxonomyUpdates'

const STEP = {
  closed: 'closed',
  main: 'main',
  extras: 'extras',
  review: 'review',
}

/** Intersection of additional category ids across selected products. */
function getCommonExtraIds(products = []) {
  if (!products.length) return []
  let common = null
  products.forEach((product) => {
    const extras = new Set(splitMainAndExtraAssignments(product).extraIds)
    if (common == null) {
      common = extras
      return
    }
    common = new Set([...common].filter((id) => extras.has(id)))
  })
  return common ? [...common] : []
}

/** Same main on every selected product — otherwise null (do not pre-focus picker). */
function getSharedMainId(products = []) {
  if (!products.length) return null
  let shared = null
  for (let i = 0; i < products.length; i += 1) {
    const { mainId } = splitMainAndExtraAssignments(products[i])
    if (!mainId) return null
    if (shared == null) {
      shared = mainId
      continue
    }
    if (shared !== mainId) return null
  }
  return shared
}

/**
 * Exact same extras set on every product (order from the first product).
 * Partial intersection is not enough — returns null if sets differ.
 */
function getIdenticalExtraIds(products = []) {
  if (!products.length) return null
  let referenceIds = null
  let referenceKey = null
  for (let i = 0; i < products.length; i += 1) {
    const ids = splitMainAndExtraAssignments(products[i]).extraIds
    const key = [...ids].sort().join('\0')
    if (referenceKey == null) {
      referenceIds = ids
      referenceKey = key
      continue
    }
    if (key !== referenceKey) return null
  }
  return referenceIds
}

function productMissesAnyExtra(product, extraIds = []) {
  const split = splitMainAndExtraAssignments(product)
  const have = new Set(split.extraIds)
  return extraIds.some((id) => id && id !== split.mainId && !have.has(id))
}

function ProductsBulkTaxonomyActions({
  selectedProducts = [],
  nodes = [],
  nodesById = {},
  onDone,
  onClearSelection,
}) {
  const [step, setStep] = useState(STEP.closed)
  const [mainId, setMainId] = useState(null)
  const [keepExistingMain, setKeepExistingMain] = useState(false)
  const [extraIds, setExtraIds] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [report, setReport] = useState(null)
  const prevSelectedCount = useRef(0)

  const withMainCount = useMemo(() => countProductsWithMain(selectedProducts), [selectedProducts])
  const withoutMainCount = selectedProducts.length - withMainCount
  const allHaveMain = selectedProducts.length > 0 && withoutMainCount === 0
  const canSkipMain = allHaveMain

  const existingMainIds = useMemo(() => {
    const ids = new Set()
    selectedProducts.forEach((product) => {
      const split = splitMainAndExtraAssignments(product)
      if (split.mainId) ids.add(split.mainId)
    })
    return ids
  }, [selectedProducts])

  const currentMainsLine = useMemo(() => {
    const counts = {}
    selectedProducts.forEach((product) => {
      const split = splitMainAndExtraAssignments(product)
      if (!split.hasMain) return
      const path = buildCatalogTaxonomyNodePathLabel(split.mainId, nodesById) || split.mainId
      counts[path] = (counts[path] || 0) + 1
    })
    return Object.entries(counts)
      .map(([path, count]) => (count > 1 ? `«${path}» (${count})` : `«${path}»`))
      .join('; ')
  }, [selectedProducts, nodesById])

  const commonExtraIds = useMemo(() => getCommonExtraIds(selectedProducts), [selectedProducts])

  const sharedMainId = useMemo(() => getSharedMainId(selectedProducts), [selectedProducts])

  const identicalExtraIds = useMemo(
    () => getIdenticalExtraIds(selectedProducts),
    [selectedProducts]
  )
  const extrasFocusNodeId =
    identicalExtraIds && identicalExtraIds.length > 0 ? identicalExtraIds[0] : null

  const productsWithExtrasCount = useMemo(
    () =>
      selectedProducts.filter((product) => splitMainAndExtraAssignments(product).extraIds.length > 0)
        .length,
    [selectedProducts]
  )

  const lockedMainIds = useMemo(() => {
    if (keepExistingMain) return existingMainIds
    if (mainId) return new Set([mainId])
    return new Set()
  }, [keepExistingMain, existingMainIds, mainId])

  const extrasChipGroups = useMemo(() => {
    const groups = new Map()
    extraIds.forEach((id) => {
      const node = nodesById[id]
      const parentId = node?.parentId || null
      const groupKey = parentId || '__root__'
      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          parentSegments: parentId
            ? buildCatalogTaxonomyNodePathSegments(parentId, nodesById)
            : [],
          leaves: [],
        })
      }
      groups.get(groupKey).leaves.push({
        id,
        name: node?.name || id,
      })
    })
    // Order = first time user picked under that parent (extraIds order), not tree/alpha.
    return Array.from(groups.values())
  }, [extraIds, nodesById])

  useEffect(() => {
    const count = selectedProducts.length
    const wasEmpty = prevSelectedCount.current === 0
    prevSelectedCount.current = count

    if (count === 0) {
      setStep(STEP.closed)
      setMainId(null)
      setKeepExistingMain(false)
      setExtraIds([])
      return
    }

    if (wasEmpty && count > 0) {
      setReport(null)
      setMainId(null)
      setKeepExistingMain(false)
      setExtraIds([])
      setStep(STEP.main)
    }
  }, [selectedProducts])

  if (!selectedProducts.length) return null

  const resetFlow = () => {
    setStep(STEP.closed)
    setMainId(null)
    setKeepExistingMain(false)
    setExtraIds([])
  }

  const mainPath = mainId ? buildCatalogTaxonomyNodePathLabel(mainId, nodesById) || mainId : ''
  const total = selectedProducts.length
  const mainReady = keepExistingMain || Boolean(mainId)

  const statusLine = (() => {
    if (keepExistingMain) {
      return currentMainsLine
        ? `Основну категорію не змінюємо. Зараз: ${currentMainsLine}.`
        : `Основну категорію не змінюємо для ${total} товарів.`
    }
    if (!mainId) {
      if (canSkipMain) {
        return `У ${total} товарів вже є основна категорія. Оберіть нову або залиште поточну.`
      }
      if (withMainCount > 0) {
        return `Оберіть основну категорію: у ${withoutMainCount} товарів її ще немає, у ${withMainCount} — вже є.`
      }
      return `Оберіть основну категорію для ${total} товарів.`
    }
    if (withMainCount === 0) {
      return `Для ${total} товарів основну категорію буде встановлено на «${mainPath}».`
    }
    if (withoutMainCount === 0) {
      return `Для ${total} товарів основну категорію буде змінено на «${mainPath}».`
    }
    return `Для ${total} товарів: у ${withMainCount} основну категорію буде змінено на «${mainPath}», у ${withoutMainCount} — встановлено вперше.`
  })()

  const currentMainsStatusLine =
    step === STEP.main && withMainCount > 0 && !mainId && currentMainsLine
      ? `Зараз основна категорія: ${currentMainsLine}.`
      : null

  const extrasStatusLine = (() => {
    if (step !== STEP.extras && step !== STEP.review) return null
    if (extraIds.length) {
      const willAdd = selectedProducts.some((product) => productMissesAnyExtra(product, extraIds))
      return willAdd
        ? `Додаткові категорії: обрано ${extraIds.length}, нові буде додано.`
        : `Додаткові категорії: вже обрано ${extraIds.length}.`
    }
    if (productsWithExtrasCount > 0 && commonExtraIds.length === 0) {
      return 'У товарів різні додаткові категорії — спільних немає. Оберіть нові за потреби.'
    }
    return 'Додаткові категорії не обрано.'
  })()

  /** Keep-main: save only if at least one product still misses a chosen extra. */
  const hasChangesToSave = keepExistingMain
    ? extraIds.length > 0 && selectedProducts.some((product) => productMissesAnyExtra(product, extraIds))
    : Boolean(mainId)

  const removeExtra = (id) => {
    setExtraIds((prev) => prev.filter((extraId) => extraId !== id))
  }

  const seedExtrasForStep = (excludeMainIds = new Set()) => {
    const seeded = commonExtraIds.filter((id) => !excludeMainIds.has(id))
    setExtraIds(seeded)
  }

  const extrasChips =
    extrasChipGroups.length > 0 ? (
      <div className="ProductsBulkTaxonomyActions-ExtrasChips">
        <p className="ProductsBulkTaxonomyActions-ExtrasChipsLabel">Додаткові категорії</p>
        <ul className="ProductsBulkTaxonomyActions-PickedPaths">
          {extrasChipGroups.map((group) => {
            const groupKey =
              group.parentSegments.map((s) => s.id).join('>') ||
              group.leaves.map((l) => l.id).join(',')
            return (
              <li key={groupKey} className="ProductsBulkTaxonomyActions-PickedPath">
                {group.parentSegments.map((segment, index) => (
                  <React.Fragment key={`${groupKey}-p-${segment.id}`}>
                    {index > 0 ? (
                      <span className="ProductsBulkTaxonomyActions-PickedPathSep">→</span>
                    ) : null}
                    <span className="ProductsBulkTaxonomyActions-PickedPathChip ProductsBulkTaxonomyActions-PickedPathChip_ancestor">
                      <span>{segment.name}</span>
                    </span>
                  </React.Fragment>
                ))}
                {group.parentSegments.length ? (
                  <span className="ProductsBulkTaxonomyActions-PickedPathSep">→</span>
                ) : null}
                <span className="ProductsBulkTaxonomyActions-PickedPathLeaves">
                  {group.leaves.map((leaf) => (
                    <span
                      key={leaf.id}
                      className="ProductsBulkTaxonomyActions-PickedPathChip ProductsBulkTaxonomyActions-PickedPathChip_leaf"
                    >
                      <span>{leaf.name}</span>
                      <button type="button" title="Прибрати" onClick={() => removeExtra(leaf.id)}>
                        ×
                      </button>
                    </span>
                  ))}
                </span>
              </li>
            )
          })}
        </ul>
      </div>
    ) : null

  const skipToExtras = () => {
    setMainId(null)
    setKeepExistingMain(true)
    seedExtrasForStep(existingMainIds)
    setStep(STEP.extras)
  }

  const pickExtra = (node) => {
    if (lockedMainIds.has(node.id)) {
      window.alert('Це вже основна категорія обраних товарів — додатковою її призначати не потрібно.')
      return
    }
    setExtraIds((prev) =>
      prev.includes(node.id) ? prev.filter((id) => id !== node.id) : [...prev, node.id]
    )
  }

  const goToReview = () => {
    if (!hasChangesToSave) return
    setStep(STEP.review)
  }

  const handleSave = async () => {
    if (!mainReady || !hasChangesToSave) return

    if (!keepExistingMain && withMainCount > 0) {
      const confirmed = window.confirm(
        `У ${withMainCount} з ${total} товарів вже є основна категорія — її буде змінено на «${mainPath}».\n\nПродовжити?`
      )
      if (!confirmed) return
    }

    setIsLoading(true)
    try {
      const result = await applyPrimaryBulkCategorization({
        products: selectedProducts,
        mainId: keepExistingMain ? null : mainId,
        extraIds,
        nodesById,
        keepExistingMain,
      })
      setReport(result)
      resetFlow()
      if (onDone) onDone(result)
    } catch (error) {
      console.error(error)
      window.alert('Не вдалося зберегти категорії.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="ProductsBulkTaxonomyActions">
      <div className="ProductsBulkTaxonomyActions-Bar">
        <span className="ProductsBulkTaxonomyActions-Count">
          Обрано товарів: {selectedProducts.length}
        </span>
        {onClearSelection ? (
          <Button
            title="Скасувати вибір"
            theme="solid"
            fill="normal"
            size={32}
            onClick={() => {
              resetFlow()
              onClearSelection()
            }}
          />
        ) : null}
      </div>

      {step !== STEP.closed ? (
        <div className="ProductsBulkTaxonomyActions-Panel">
          {step === STEP.main ? (
            <>
              <p className="ProductsBulkTaxonomyActions-StepTitle">
                Крок 1. Оберіть основну категорію для {selectedProducts.length} товарів
              </p>
              <CatalogTaxonomyNodePicker
                key={`bulk-main-${sharedMainId || 'none'}`}
                nodes={nodes}
                nodesById={nodesById}
                assignedIds={mainId ? [mainId] : []}
                mode="select"
                hint=""
                focusNodeId={sharedMainId}
                onPick={(node) => {
                  setKeepExistingMain(false)
                  setMainId(node.id)
                }}
              />
              <div className="ProductsBulkTaxonomyActions-SummaryLine">
                <div>{statusLine}</div>
                {currentMainsStatusLine ? <div>{currentMainsStatusLine}</div> : null}
              </div>
              <div className="ProductsBulkTaxonomyActions-Apply">
                <Button
                  title="Далі"
                  theme="solid"
                  fill="accent"
                  size={32}
                  onClick={() => {
                    if (!mainId) {
                      window.alert(
                        canSkipMain
                          ? 'Оберіть нову основну або натисніть «Залишити поточну».'
                          : 'Спочатку оберіть основну категорію для обраних товарів.'
                      )
                      return
                    }
                    setKeepExistingMain(false)
                    seedExtrasForStep(new Set([mainId]))
                    setStep(STEP.extras)
                  }}
                />
                {canSkipMain ? (
                  <Button
                    title="Залишити поточну"
                    theme="solid"
                    fill="normal"
                    size={32}
                    onClick={skipToExtras}
                  />
                ) : null}
              </div>
            </>
          ) : null}

          {step === STEP.extras ? (
            <>
              <p className="ProductsBulkTaxonomyActions-StepTitle">
                {keepExistingMain
                  ? 'Крок 2. Оберіть додаткові категорії'
                  : 'Крок 2. Додаткові категорії (не обовʼязково)'}
              </p>
              <CatalogTaxonomyNodePicker
                key={`bulk-extras-${extrasFocusNodeId || 'none'}`}
                nodes={nodes}
                nodesById={nodesById}
                assignedIds={
                  keepExistingMain ? extraIds : [mainId, ...extraIds].filter(Boolean)
                }
                mode="select"
                hint=""
                focusNodeId={extrasFocusNodeId}
                onPick={pickExtra}
              />
              {extrasChips}
              <div className="ProductsBulkTaxonomyActions-SummaryLine">
                <div>{statusLine}</div>
                {extrasStatusLine ? <div>{extrasStatusLine}</div> : null}
              </div>
              <div className="ProductsBulkTaxonomyActions-Apply">
                <Button
                  title="Назад"
                  theme="solid"
                  fill="normal"
                  size={32}
                  onClick={() => {
                    setKeepExistingMain(false)
                    setStep(STEP.main)
                  }}
                />
                {hasChangesToSave ? (
                  <Button
                    title="Далі"
                    theme="solid"
                    fill="accent"
                    size={32}
                    onClick={goToReview}
                  />
                ) : null}
              </div>
            </>
          ) : null}

          {step === STEP.review ? (
            <>
              <p className="ProductsBulkTaxonomyActions-StepTitle">Крок 3. Збереження</p>
              {extrasChips}
              <div className="ProductsBulkTaxonomyActions-SummaryLine">
                {hasChangesToSave ? (
                  <>
                    <div>{statusLine}</div>
                    {extrasStatusLine ? <div>{extrasStatusLine}</div> : null}
                  </>
                ) : (
                  <div>
                    Немає змін для збереження. Поверніться й оберіть додаткові категорії або змініть
                    основну категорію.
                  </div>
                )}
              </div>
              <div className="ProductsBulkTaxonomyActions-Apply">
                <Button
                  title="Назад"
                  theme="solid"
                  fill="normal"
                  size={32}
                  onClick={() => setStep(STEP.extras)}
                />
                {hasChangesToSave ? (
                  <Button
                    title={`Зберегти категорії для ${total} товарів`}
                    theme="solid"
                    fill="accent"
                    size={32}
                    isLoading={isLoading}
                    onClick={handleSave}
                  />
                ) : null}
              </div>
            </>
          ) : null}
        </div>
      ) : null}

      {report ? (
        <div className="ProductsBulkTaxonomyActions-Report">
          <p>
            Категорії збережено для {report.counts.updated} товарів. Пропущено:{' '}
            {report.counts.skipped}. Помилки: {report.counts.failed}.
          </p>
          {report.skipped.slice(0, 8).map((item) => (
            <p key={item.id}>
              Пропущено {item.name || item.id}: {item.reason}
            </p>
          ))}
        </div>
      ) : null}
    </div>
  )
}

export default ProductsBulkTaxonomyActions
